import React, { useState, useEffect, useRef } from 'react';
import { Menu, X, ArrowRight, Mail, Instagram, CheckCircle, Clock, TrendingUp, User, Code, Quote } from 'lucide-react';
import { FaTiktok } from 'react-icons/fa';

// --- Data for Navigation/Footer Consistency ---
const services = [
  {
    id: 'dev',
    title: 'Website Design and Development',
    link: '/web-development',
  },
  {
    id: 'revamp',
    title: 'Website Revamp',
    link: '/website-revamp',
  },
  {
    id: 'hosting',
    title: 'Server Hosting & Maintenance',
    link: '/hosting-maintenance',
  },
];

// --- Case Study Specific Data: Foundation Commercial Cleaning ---
const cleaningProject = {
  title: "Foundation Commercial Cleaning",
  tagline: "Spotless Environments, Spotless Website.",
  client: "Foundation Commercial Cleaning",
  service: "Website Design, Development and Maintenance",
  duration: "3 Weeks Build Time",
  heroImage: "/foundationhome.png", 
  
  testimonial: {
    quote: "Vyxion Labs was amazing to work with. From our first call to the consultation and all the way through deployment, the entire website process was smooth and stress-free. If you’re a business owner looking for someone reliable to build your website I highly recommend them. As my business grows I will continue to work with Vyxion Labs for all of my website needs.",
    author: "Brelle - Owner",
    company: "Foundation Commercial Cleaning"
  },

  videoReview: "/foundationreview.mp4",

  showcase: {
    mainFeatures: [
        { 
            id: 1, 
            src: ["/foundation3.png", "/foundation4.png"], 
            isSlider: true,
            caption: "Premium Feel: A clean display of services offered with a dark overlay to give a premium feel. Accompanied by a clear project timeline.", 
            title: "Service Showcases"
        },
        { 
            id: 2, 
            src: "/foundationscrolling.mp4", 
            isVideo: true,                  
            caption: "Mobile Responsive: A smooth user experience with responsive and animated elements that are  optimised for mobile.", 
            title: "Seamless Scrolling Experience" 
        },
        { 
            id: 3, 
            src: ["/foundation1.png"], 
            isAnimated: false, 
            caption: "Business Location: High-resolution map embeddings that show clients the area where services are provided.", 
            title: "Map Embedding"
        },
    ],
    featureImage: { 
      src: "/foundation5.png", 
      caption: "Contact Form: A lead capturing form that is connected to the business e-mail for fast notifications and effortless communication." 
    },
},
  
  results: {
    heading: "The Impact: Clean Code, Better Business",
    points: [
      { icon: <TrendingUp className="w-6 h-6 text-cyan-500" />, text: "Quote Requests: 55% increase in commercial cleaning inquiries within the first 90 days." },
      { icon: <CheckCircle className="w-6 h-6 text-cyan-500" />, text: "SEO Ranking: Secured Page 1 rankings for key local janitorial service keywords." },
      { icon: <Clock className="w-6 h-6 text-cyan-500" />, text: "User Engagement: Average session duration increased by 150% due to intuitive navigation." },
    ],
  },
};

// --- Reusable Components ---

const AnimatedSection = ({ children, animationClass = 'animate-slideUp', delay = '0ms', className = '' }) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => { if (ref.current) observer.unobserve(ref.current); };
  }, []);

  return (
    <div
      ref={ref}
      className={`${className} ${isVisible ? animationClass : 'opacity-0'}`}
      style={{ animationDelay: isVisible ? delay : '0ms' }}
    >
      {children}
    </div>
  );
};

const TwoImageSlider = ({ images }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex === images.length - 1 ? 0 : prevIndex + 1));
    }, 4000);
    return () => clearInterval(interval);
  }, [images.length]);

  if (images.length !== 2) return null;
  return (
    <div className="relative w-full h-full overflow-hidden">
      <div 
        className="flex transition-transform duration-700 ease-in-out"
        style={{ width: '200%', transform: `translateX(-${currentIndex * 50}%)` }}
      >
        <img src={images[0]} alt="Slide 1" className="w-1/2 h-auto object-cover flex-shrink-0" />
        <img src={images[1]} alt="Slide 2" className="w-1/2 h-auto object-cover flex-shrink-0" />
      </div>
    </div>
  );
};

const FooterSection = () => {
    return (
        <footer className="bg-slate-900/90 backdrop-blur-sm text-slate-300 border-t border-slate-700/50 py-16 px-6">
            <div className="max-w-7xl mx-auto">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-12 border-b border-slate-700 pb-12">
                    <div className="col-span-2 md:col-span-2 space-y-6">
                        <a href="/" className="flex items-center gap-3">
                            <img src="/logo.png" alt="SR logo" className="w-12 h-12 object-cover rounded-xl" />
                            <div>
                                <div className="text-xl font-bold text-white">Vyxion Labs</div>
                                <div className="text-sm text-slate-400">Digital Presence. Engineered.</div>
                            </div>
                        </a>
                        <p className="text-sm text-slate-400 max-w-xs">
                            We are committed to delivering outstanding digital solutions that combine stunning design with robust performance.
                        </p>
                        <div className="flex space-x-4 pt-2">
                            <a href="https://www.tiktok.com/@vyxion.labs" className="text-slate-400 hover:text-cyan-500 transition-colors" aria-label="TikTok"><FaTiktok size={20} /></a>
                            <a href="https://www.instagram.com/vyxionlabs" className="text-slate-400 hover:text-cyan-500 transition-colors" aria-label="Instagram"><Instagram size={20} /></a>
                        </div>
                    </div>
                    <div className="space-y-4">
                        <h4 className="text-lg font-bold text-white mb-4">Quick Links</h4>
                        <ul className="space-y-3">
                            <li><a href="/consult" className="text-sm hover:text-cyan-500 transition-colors">Book a Free Consult</a></li>
                            <li><a href="/pricing" className="text-sm hover:text-cyan-500 transition-colors">Pricing</a></li>
                            <li><a href="/ourworks" className="text-sm hover:text-cyan-500 transition-colors">Our Works</a></li>
                            <li><a href="/faq" className="text-sm hover:text-cyan-500 transition-colors">FAQ</a></li>
                            <li><a href="/about" className="text-sm hover:text-cyan-500 transition-colors">About Us</a></li>
                        </ul>
                    </div>
                    <div className="space-y-4">
                        <h4 className="text-lg font-bold text-white mb-4">Services</h4>
                        <ul className="space-y-3">
                            {services.map((s, index) => (
                                <li key={index}><a href={s.link} className="text-sm hover:text-cyan-500 transition-colors">{s.title}</a></li>
                            ))}
                        </ul>
                    </div>
                    <div className="space-y-4 col-span-2 md:col-span-1">
                        <h4 className="text-lg font-bold text-white mb-4">Contact</h4>
                        <ul className="space-y-3">
                            <li className="flex items-center gap-3">
                                <Mail size={16} className="text-cyan-500 flex-shrink-0" />
                                <a href="mailto:vyxionlabs.info@gmail.com" className="text-sm hover:text-cyan-500 transition-colors">vyxionlabs.info@gmail.com</a>
                            </li>
                        </ul>
                    </div>
                </div>
                <div className="flex justify-center md:justify-start space-x-6 mt-8">
                    <a href="/privacy" className="text-xs text-slate-500 hover:text-cyan-500 transition-colors font-medium">Privacy Policy</a>
                </div>
                <div className="mt-4 text-center text-sm text-slate-500">
                    &copy; {new Date().getFullYear()} Vyxion Labs. All rights reserved.
                </div>
            </div>
        </footer>
    );
};

// --- Main Component ---
export default function FoundationCleaningCaseStudy() {
  const [isContactPopupOpen, setIsContactPopupOpen] = useState(false);
  const [isServicesDropdownOpen, setIsServicesDropdownOpen] = useState(false);
  const servicesDropdownRef = useRef(null);

  const handleServicesClick = (e) => {
    e.stopPropagation();
    setIsServicesDropdownOpen(!isServicesDropdownOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (servicesDropdownRef.current && !servicesDropdownRef.current.contains(event.target)) {
        setIsServicesDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [servicesDropdownRef]);

  const handleMenuClick = () => setIsContactPopupOpen(!isContactPopupOpen);

  return (
    <div className="bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50 text-slate-900 min-h-screen antialiased relative overflow-x-hidden">
      
      <style>{`
        @keyframes slideUp { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
        .animate-slideUp { animation: slideUp 0.6s ease-out forwards; }
        .modal-overlay { position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0, 0, 0, 0.4); backdrop-filter: blur(8px); z-index: 9999; opacity: 0; visibility: hidden; transition: opacity 0.3s ease, visibility 0.3s ease; }
        .modal-overlay.open { opacity: 1; visibility: visible; }
        .modal-content { position: fixed; top: 0; right: 0; width: 90%; max-width: 400px; height: 100%; background: rgba(255, 255, 255, 0.95); backdrop-filter: blur(30px); box-shadow: -10px 0 30px rgba(0, 0, 0, 0.2); transform: translateX(100%); transition: transform 0.4s cubic-bezier(0.86, 0, 0.07, 1); overflow-y: auto; padding: 3rem 1.5rem; }
        .modal-overlay.open .modal-content { transform: translateX(0); }
      `}</style>
      
      {/* Header */}
      <header className="w-full z-[100] border-b border-neutral-200 bg-white sticky top-0">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-20">
            <a href="/" className="flex items-center gap-3 z-50 flex-shrink-0">
              <div className="relative w-10 h-10 md:w-14 md:h-14 flex items-center justify-center">
                <img src="/logo.png" alt="SR logo" className="w-full h-full object-cover rounded-sm" />
              </div>
              <div>
                <div className="text-lg font-bold text-neutral-900">Vyxion Labs</div>
                <div className="text-[10px] md:text-xs text-neutral-600 font-medium">Digital Presence. Engineered.</div>
              </div>
            </a>
            <nav className="hidden lg:flex flex-1 justify-center items-center gap-6 xl:gap-8 px-4">
                <a href="/" className="text-neutral-700 hover:text-cyan-600 transition-colors font-medium">Home</a>
                <div className="relative" ref={servicesDropdownRef}>
                    <span className="text-neutral-700 hover:text-cyan-600 transition-colors font-medium cursor-pointer" onClick={handleServicesClick}>Services</span>
                    {isServicesDropdownOpen && (
                   <div className="absolute left-1/2 transform -translate-x-1/2 mt-2 w-64 bg-white rounded-sm border border-neutral-200 overflow-hidden z-[110]">
                        <div className="flex flex-col p-2">
                            <a href="/web-development" className="p-3 block rounded-sm hover:bg-neutral-100 transition-colors text-sm font-medium text-neutral-800">Website Design & Development</a>
                            <a href="/website-revamp" className="p-3 block rounded-sm hover:bg-neutral-100 transition-colors text-sm font-medium text-neutral-800">Website Revamp</a>
                            <a href="/hosting-maintenance" className="p-3 block rounded-sm hover:bg-neutral-100 transition-colors text-sm font-medium text-neutral-800">Server Hosting & Maintenance</a>
                        </div>
                    </div>
                    )}
                </div>
                <a href="/pricing" className="text-neutral-700 hover:text-cyan-600 transition-colors font-medium">Pricing</a>
                <a href="/about" className="text-neutral-700 hover:text-cyan-600 transition-colors font-medium">About Us</a>
                <a href="/ourworks" className="text-neutral-900 font-bold">Our Works</a>
                <a href="/faq" className="text-neutral-700 hover:text-cyan-600 transition-colors font-medium">FAQ</a>
            </nav>
            <div className="flex items-center gap-3 md:gap-4 flex-shrink-0">
                <a href="/consult" className="hidden lg:flex group relative px-5 py-2 bg-gray-800 text-gray-200 font-semibold rounded-md hover:bg-gray-900 transition-all text-sm shadow-sm">
                  <span className="relative z-10 flex items-center gap-2">Free Consult <ArrowRight className="w-4 h-4 group-hover:translate-x-1" /></span>
                </a>
                <div className="flex items-center gap-3 border-l border-neutral-200 pl-3 md:pl-4">
                    <a href="https://www.tiktok.com/@vyxion.labs" className="text-neutral-600 hover:text-black"><FaTiktok className="w-4 h-4" /></a>
                    <a href="https://www.instagram.com/vyxionlabs" className="text-neutral-600 hover:text-pink-600"><Instagram className="w-4 h-4" /></a>
                </div>
                <button className="lg:hidden flex items-center gap-1 px-3 py-2 text-neutral-700 font-semibold rounded-sm border border-neutral-300 bg-white ml-2" onClick={handleMenuClick}>
                    <Menu className="w-5 h-5" />
                </button>
            </div>
          </div>
        </div>
      </header>
      
      {/* Mobile Menu Overlay */}
      <div className={`modal-overlay ${isContactPopupOpen ? 'open' : ''}`} onClick={handleMenuClick}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="absolute top-6 right-6 p-2 rounded-sm bg-neutral-100" onClick={handleMenuClick}><X size={24} /></button>
            <h2 className="text-3xl font-bold mb-8 mt-12">Navigation</h2>
            <div className="space-y-4 mb-10 border-b pb-8">
                <div className="space-y-3 pl-4 border-l-2 border-purple-200">
                     <p className="text-xs font-bold text-neutral-400 uppercase">Services</p>
                     <a href="/web-development" className="block font-medium text-neutral-600">Web Development</a>
                     <a href="/website-revamp" className="block font-medium text-neutral-600">Website Revamp</a>
                     <a href="/hosting-maintenance" className="block font-medium text-neutral-600">Hosting</a>
                </div>
                <a href="/pricing" className="block text-2xl font-bold text-neutral-800">Pricing</a>
                <a href="/" className="block text-2xl font-bold text-neutral-800">Home</a>
                <a href="/faq" className="block text-2xl font-bold text-neutral-800">FAQ</a>
                <a href="/about" className="block text-2xl font-bold text-neutral-800">About Us</a>
            </div>
        </div>
      </div>

      <main className="relative z-10">
        
        {/* --- 1. HERO SECTION --- */}
        <section className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
          <AnimatedSection className="text-center space-y-6 max-w-4xl mx-auto">
            <h1 className="text-5xl lg:text-7xl font-black text-slate-900 leading-tight">
              {cleaningProject.title}
            </h1>
            <p className="text-xl text-slate-700 font-medium">
              {cleaningProject.tagline}
            </p>
            <div className="flex justify-center items-center gap-6 pt-4 text-slate-600 flex-wrap">
              <span className="flex items-center gap-2 text-sm"><User className="w-4 h-4 text-cyan-500" /> Client: {cleaningProject.client}</span>
              <span className="flex items-center gap-2 text-sm"><Code className="w-4 h-4 text-cyan-500" /> Service: {cleaningProject.service}</span>
              <span className="flex items-center gap-2 text-sm"><Clock className="w-4 h-4 text-cyan-500" /> Duration: {cleaningProject.duration}</span>
            </div>
          </AnimatedSection>

          <AnimatedSection className="mt-16 mx-auto max-w-4xl rounded-3xl overflow-hidden shadow-2xl border-4 border-white/50">
            <img src={cleaningProject.heroImage} alt="Foundation Hero" className="w-full h-auto object-cover" />
          </AnimatedSection>
        </section>

        {/* --- 2. CLIENT TESTIMONIAL SECTION (Reordered to be higher) --- */}
        <section className="bg-slate-900 py-20 my-10">
    <div className="max-w-5xl mx-auto px-6">
        <AnimatedSection className="relative bg-slate-800/50 p-10 md:p-16 rounded-3xl border border-slate-700">
            <Quote className="absolute top-8 left-8 w-12 h-12 text-cyan-500/20" />
            
            <div className="relative z-10 space-y-8">
                <p className="text-2xl md:text-3xl font-medium text-slate-100 italic leading-relaxed">
                    "{cleaningProject.testimonial.quote}"
                </p>
                
                {/* Line removed and layout simplified */}
                <div>
                    <div className="text-lg font-bold text-white">
                        {cleaningProject.testimonial.author}
                    </div>
                    <div className="text-cyan-500">
                        {cleaningProject.testimonial.company}
                    </div>
                </div>
            </div>
        </AnimatedSection>
    </div>
</section>

        {/* --- 3. VIDEO REVIEW SECTION --- */}
        <section className="max-w-xl mx-auto px-6 lg:px-8 py-20">
  <AnimatedSection className="text-center mb-12">
    <h2 className="text-4xl font-extrabold text-slate-900">
     Client Testimony
    </h2>
    <p className="text-lg text-slate-600 mt-2">
      A short video testimony from Foundation Commercial Cleaning.
    </p>
  </AnimatedSection>

  {/* Changed to max-w-sm for a smaller, more compact video player */}
  <AnimatedSection className="mx-auto rounded-3xl overflow-hidden shadow-2xl border-8 border-white max-w-sm">
    <video
      controls
      className="w-full h-auto shadow-inner"
    >
      <source src={cleaningProject.videoReview} type="video/mp4" />
      Your browser does not support the video tag.
    </video>
  </AnimatedSection>
</section>



        {/* --- 4. SHOWCASE SECTIONS --- */}
        <section className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
    <div className="space-y-20">
        {cleaningProject.showcase.mainFeatures.map((item, index) => (
            <AnimatedSection key={item.id} className={`grid lg:grid-cols-12 gap-10 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                
                {/* Media Column - Added flex and justify-center to balance the video in the space */}
                <div className={`lg:col-span-7 flex justify-center ${index % 2 === 1 ? 'lg:order-2' : 'lg:order-1'}`}>
                    {item.isSlider ? (
                        <div className="w-full rounded-2xl overflow-hidden shadow-2xl border border-white/50">
                            <TwoImageSlider images={item.src} />
                        </div>
                    ) : item.isVideo ? (
                        /* This container is now perfectly centered within the col-span-7 space */
                        <div className="max-w-xs rounded-2xl overflow-hidden shadow-2xl border-8 border-white">
                            <video
                                autoPlay
                                loop
                                muted
                                playsInline
                                className="w-full h-auto object-cover pointer-events-none"
                            >
                                <source src={item.src} type="video/mp4" />
                                Your browser does not support the video tag.
                            </video>
                        </div>
                    ) : (
                        /* Standard Image */
                        <div className="w-full rounded-2xl overflow-hidden shadow-2xl border border-white/50">
                            <img src={item.src} alt={item.title} className="w-full h-auto object-cover transition-transform hover:scale-[1.02]" />
                        </div>
                    )}
                </div>

                {/* Text Column */}
                <div className={`lg:col-span-5 space-y-4 ${index % 2 === 1 ? 'lg:order-1' : 'lg:order-2'}`}>
                    <span className="text-sm font-bold uppercase tracking-widest text-cyan-600">{item.title}</span>
                    <h3 className="text-3xl font-extrabold text-slate-900">{item.caption.split(':')[0]}</h3>
                    <p className="text-lg text-slate-700">{item.caption.split(':')[1]}</p>
                </div>

            </AnimatedSection>
        ))}
    </div>
</section>

        {/* --- 5. BOOKING/FEATURE SHOWCASE --- */}
        <section className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
            <AnimatedSection  className="grid lg:grid-cols-3 gap-12 items-center bg-white p-12 lg:p-16 rounded-3xl shadow-2xl border border-slate-100">
                <div className="lg:col-span-1 space-y-6">
                    <h2 className="text-4xl font-extrabold text-cyan-600">Growth-Driven Functionality</h2>
                    <p className="text-lg text-slate-700">{cleaningProject.showcase.featureImage.caption}</p>
                  
                </div>
                <div className="lg:col-span-2 rounded-xl overflow-hidden shadow-2xl border border-slate-200">
                    <img src={cleaningProject.showcase.featureImage.src} alt="Booking Feature" className="w-full h-auto object-cover" />
                </div>
            </AnimatedSection>
        </section>

        

        {/* --- 7. CTA SECTION --- */}
        <section className="max-w-7xl mx-auto px-6 lg:px-8 pb-20">
            <AnimatedSection  className="text-center py-16 bg-white rounded-3xl shadow-2xl border border-slate-100 mx-auto max-w-5xl my-16 space-y-6">
              <h2 className="text-4xl font-extrabold text-slate-900">Ready to Scale Your Local Business?</h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">Join Foundation Commercial Cleaning and other industry leaders in establishing a dominant digital presence.</p>
              <a href="/consult" className="group inline-flex items-center gap-2 px-8 py-4 bg-cyan-500 hover:bg-cyan-600 text-white font-bold rounded-full shadow-lg transition-all hover:scale-105 text-lg">
                <span className="flex items-center gap-2">Book Your Free Consultation <ArrowRight className="w-5 h-5 group-hover:translate-x-1" /></span>
              </a>
            </AnimatedSection>
        </section>

        <FooterSection />
      </main>
    </div>
  );
}